import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { AlertTriangle, Check, Copy, RefreshCw, RotateCw, Sparkles } from "lucide-react";
import { cn } from "../../ui/utils";
import { AssistantMascot } from "./AssistantMascot";
import { ChatMarkdown } from "./ChatMarkdown";
import { SuggestionChip } from "./SuggestionCard";
import type { AssistantProgress } from "../store";
import type { ChatMessage } from "../types";
import {
  ENABLE_TYPING_PREVIEW,
  joinTokens,
  segmentMarkdown,
  useRotatingThinkingMessage,
  useSegmentReveal,
  type MarkdownSegment,
  type RevealPos,
} from "../lib/typingPreview";

const VERB_MAP: Record<string, string> = {
  list: "Listing",
  get: "Fetching",
  fetch: "Fetching",
  search: "Searching",
  find: "Finding",
  compare: "Comparing",
  create: "Creating",
  update: "Updating",
  delete: "Removing",
  resolve: "Resolving",
  summarize: "Summarizing",
};

function prettifyTool(name: string): string {
  const tail = name.includes("-") ? name.split("-").slice(1).join("-") : name;
  const words = tail.replace(/_/g, " ").trim();
  if (!words) return "Working";
  const [first, ...rest] = words.split(" ");
  const verb = VERB_MAP[first.toLowerCase()];
  if (verb) return `${verb} ${rest.join(" ")}`.trim();
  return first.charAt(0).toUpperCase() + first.slice(1) + (rest.length ? " " + rest.join(" ") : "");
}

function progressLabel(p: AssistantProgress | null | undefined): string {
  if (!p) return "Thinking";
  if (p.lastTool) return `${prettifyTool(p.lastTool)}`;
  if (p.iteration > 1) return `Thinking - step ${p.iteration}`;
  return "Thinking";
}

function formatTime(ts: number): string {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

const EASE_OUT = [0.16, 1, 0.3, 1] as const;

const enter = {
  initial: { opacity: 0, y: 8, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  transition: { type: "spring", stiffness: 320, damping: 26 },
} as const;

function renderInline(text: string): ReactNode[] {
  return text.split("**").map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold">
        {part}
      </strong>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

function AssistantAvatar({ inspect = false }: { inspect?: boolean }) {
  // Decorative: the avatar repeats before every assistant turn, so it is hidden
  // from assistive tech (the polite live region already conveys the replies).
  return (
    <span aria-hidden className="relative mt-0.5 flex size-8 shrink-0 items-center justify-center">
      <AssistantMascot
        className="size-8 drop-shadow-[0_1px_4px_rgba(124,58,237,0.22)]"
        animated={inspect}
        inspect={inspect}
        hoverInspect={false}
      />
    </span>
  );
}

const SKELETON_LINES = ["94%", "82%", "68%"];

function SkeletonLine({ width, delay = 0 }: { width: string; delay?: number }) {
  return (
    <span
      className="relative block h-2.5 overflow-hidden rounded-full"
      style={{ width, background: "var(--ca-skel-base)" }}
    >
      <span
        aria-hidden
        className="absolute inset-0 animate-ca-shimmer"
        style={{
          background: "linear-gradient(90deg, transparent, var(--ca-skel-hi), transparent)",
          animationDelay: `${delay}s`,
        }}
      />
    </span>
  );
}

export function TypingIndicator({ progress }: { progress?: AssistantProgress | null }) {
  const hasTool = !!progress?.lastTool;
  const rotating = useRotatingThinkingMessage(ENABLE_TYPING_PREVIEW && !hasTool);
  const label = hasTool || !ENABLE_TYPING_PREVIEW ? progressLabel(progress) : rotating;

  return (
    <motion.div
      {...enter}
      exit={{ opacity: 0, y: -4, transition: { duration: 0.18, ease: "easeOut" } }}
      className="flex flex-col items-start gap-2"
      aria-hidden
    >
      <AssistantAvatar inspect />
      <div className="w-full min-w-0 pt-0.5">
        <span className="flex items-center gap-2">
          <span className="relative inline-flex text-[0.8125rem] font-medium">
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={label}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                className="ca-thinking-shimmer"
              >
                {label}
              </motion.span>
            </AnimatePresence>
          </span>
          <span className="flex items-center gap-1" aria-hidden>
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="size-1 animate-bounce rounded-full bg-primary/55 motion-reduce:animate-none"
                style={{ animationDelay: `${i * 0.15}s`, animationDuration: "0.9s" }}
              />
            ))}
          </span>
        </span>

        <div className="mt-3 max-w-[90%] space-y-2.5">
          {SKELETON_LINES.map((w, i) => (
            <SkeletonLine key={i} width={w} delay={i * 0.12} />
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function ActionButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        "flex size-7 items-center justify-center rounded-lg text-muted-foreground transition-colors",
        "hover:bg-accent hover:text-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
      )}
    >
      {children}
    </button>
  );
}

interface MessageBubbleProps {
  message: ChatMessage;
  onSend: (prompt: string) => void;
  onRetry: (message: ChatMessage) => void;
  isLast?: boolean;
}

export function MessageBubble({ message, onSend, onRetry, isLast = false }: MessageBubbleProps) {
  const isUser = message.role === "user";

  if (isUser) {
    return (
      <motion.div {...enter} className="ca-msg flex flex-col items-end">
        <div
          className={cn(
            "relative max-w-[85%] rounded-2xl rounded-tr-md px-3.5 py-2.5 text-white",
            "bg-gradient-to-br from-indigo-600 to-violet-600 shadow-[0_4px_14px_-6px_rgba(99,102,241,0.55)]",
            "dark:from-indigo-500/35 dark:to-violet-500/30 dark:text-white/95 dark:shadow-[0_8px_24px_-14px_rgba(124,58,237,0.5)] dark:ring-1 dark:ring-inset dark:ring-white/10 dark:backdrop-blur-md",
          )}
        >
          <p className="whitespace-pre-wrap text-[0.8125rem] leading-relaxed">
            {message.text ? renderInline(message.text) : ""}
          </p>
        </div>
        <span className="mt-1 px-1 text-[0.6875rem] font-medium tabular-nums text-muted-foreground">
          {formatTime(message.createdAt)}
        </span>
      </motion.div>
    );
  }

  if (message.error) {
    return (
      <motion.div {...enter} className="flex flex-col items-start gap-2">
        <AssistantAvatar />
        <div className="w-full min-w-0">
          <div className="flex items-start gap-2.5 rounded-xl border border-red-200/70 bg-red-50/70 px-3.5 py-2.5 dark:border-red-500/25 dark:bg-red-500/[0.08]">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-500" />
            <p className="text-[0.8125rem] leading-relaxed text-foreground/80">
              {message.text ?? "Something went wrong while generating a response."}
            </p>
          </div>
          {message.sourcePrompt && (
            <button
              type="button"
              onClick={() => onRetry(message)}
              className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card/60 px-3 py-1.5 text-[0.75rem] font-medium text-foreground shadow-sm transition-all hover:-translate-y-px hover:border-primary/40 hover:bg-primary/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background dark:border-white/10 dark:bg-white/[0.05]"
            >
              <RefreshCw className="size-3.5" />
              Try again
            </button>
          )}
        </div>
      </motion.div>
    );
  }

  return <AssistantBubble message={message} onSend={onSend} onRetry={onRetry} isLast={isLast} />;
}

const played = new Set<string>();

function RevealedMarkdown({
  segments,
  pos,
  done,
}: {
  segments: MarkdownSegment[];
  pos: RevealPos;
  done: boolean;
}) {
  return (
    <div className="space-y-3">
      {segments.map((seg, i) => {
        if (i > pos.seg) return null;
        if (seg.kind === "block") {
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE_OUT }}
            >
              <ChatMarkdown content={seg.text} />
            </motion.div>
          );
        }
        const isCurrent = i === pos.seg && !done;
        const text = isCurrent ? joinTokens(seg.tokens, pos.tok) : seg.text;
        return (
          <div key={i} className={isCurrent ? "ca-caret" : undefined}>
            <ChatMarkdown content={text} />
          </div>
        );
      })}
    </div>
  );
}

function AssistantBubble({ message, onSend, onRetry, isLast = false }: MessageBubbleProps) {
  const reduceMotion = useReducedMotion();
  const [copied, setCopied] = useState(false);

  const alreadyPlayedRef = useRef(played.has(message.id));

  const animateEnabled =
    ENABLE_TYPING_PREVIEW &&
    !!message.animate &&
    isLast &&
    !reduceMotion &&
    !alreadyPlayedRef.current;

  const segments = useMemo(() => segmentMarkdown(message.text ?? ""), [message.text]);
  const { pos, done, skip } = useSegmentReveal(segments, animateEnabled);

  useEffect(() => {
    if (animateEnabled && done) played.add(message.id);
  }, [animateEnabled, done, message.id]);

  const revealing = animateEnabled && !done;
  const showExtras = !animateEnabled || done;

  const copy = () => {
    if (!message.text) return;
    void navigator.clipboard?.writeText(message.text).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  };

  const canRegenerate = isLast && !!message.sourcePrompt;
  const hasFollowUps = !!message.followUps && message.followUps.length > 0;

  return (
    <motion.div {...enter} className="ca-msg flex flex-col items-start gap-2">
      <AssistantAvatar />
      <div className="w-full min-w-0">
        <div
          onClick={revealing ? skip : undefined}
          className={cn(
            "text-[0.8125rem] leading-relaxed text-foreground",
            revealing && "cursor-default",
          )}
        >
          {message.text &&
            (animateEnabled ? (
              <RevealedMarkdown segments={segments} pos={pos} done={done} />
            ) : (
              <ChatMarkdown content={message.text} />
            ))}
        </div>

        {showExtras && (
          <div className="-ml-1.5 mt-1.5 flex items-center gap-0.5">
            <ActionButton label={copied ? "Copied" : "Copy"} onClick={copy}>
              {copied ? <Check className="size-3.5 text-emerald-500" /> : <Copy className="size-3.5" />}
            </ActionButton>
            {canRegenerate && (
              <ActionButton label="Regenerate" onClick={() => onRetry(message)}>
                <RotateCw className="size-3.5" />
              </ActionButton>
            )}
            <span className="ml-1 text-[0.6875rem] font-medium tabular-nums text-muted-foreground">
              {formatTime(message.createdAt)}
            </span>
          </div>
        )}

        {showExtras && isLast && hasFollowUps && (
          <div className="mt-3.5">
            <p className="mb-2 flex items-center gap-1.5 text-[0.6875rem] font-semibold tracking-wider text-muted-foreground">
              <Sparkles className="size-3.5 text-primary" />
              Suggested
            </p>
            <div className="flex flex-wrap gap-1.5">
              {message.followUps!.map((f, i) => (
                <SuggestionChip key={i} title={f} onClick={() => onSend(f)} />
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
